/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnInit, inject} from '@angular/core';
import {IxActiveModal} from '@siemens/ix-angular';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {BatteryPassAttributeLabel} from '../../../../models/BatteryPassAttributeLabel';
import {ApiAssignmentPatchDto} from '../../../../models/battery/AssignmentPatchDto';
import {GeneralDataHandlingService} from '../../../../services/general.data-handling.service';
import {PassportService} from '../../../../services/passport.service';
import {TranslateService} from '@ngx-translate/core';
import {PassportDto} from '../../../../models/Passport';

const SEARCH_ASSETS_TAB = 1;
const COMPARE_ASSETS_TAB = 2;
const IMPORT_ASSETS_TAB = 3;

@Component({
  selector: 'app-modal-import-battery-data',
  templateUrl: './modal-import-battery-data.component.html',
  styleUrl: './modal-import-battery-data.component.scss',
  standalone: false,
})
export class ModalImportBatteryDataComponent implements OnInit {
  readonly activeModal = inject(IxActiveModal);
  private readonly dataHandlingService = inject(GeneralDataHandlingService);
  private readonly passportService = inject(PassportService);
  private readonly translate = inject(TranslateService);

  SEARCH_ASSETS_TAB = SEARCH_ASSETS_TAB;
  COMPARE_ASSETS_TAB = COMPARE_ASSETS_TAB;
  IMPORT_ASSETS_TAB = IMPORT_ASSETS_TAB;

  providers: string[] = [];
  initialAttributeLabelData: BatteryPassAttributeLabel[] = [];
  receivedData: {key: string; value: string; found: boolean}[] = [];
  expectedData: {key: string; value: string; found: boolean}[] = [];
  model: PassportDto = {} as PassportDto;

  selectedSchema = '';

  cxSearchForm!: FormGroup;
  searchErrors: string[] = [];
  importErrors: string[] = [];
  selectedIndex = this.SEARCH_ASSETS_TAB;
  importSearchLoading = false;
  importLoading = false;
  isSomethingToMap = false;
  isImportSuccessful = false;
  hasImportStarted = false;

  availableSchemas = [
    {
      value: 'cx_battery_5.0.0',
      label: this.translate.instant('battery_pass.edit.schemas.cx_battery_5'),
    },
    {
      value: 'cx_battery_6.0.0',
      label: this.translate.instant('battery_pass.edit.schemas.cx_battery_6'),
    },
    {
      value: 'cx_pcf_7.0.0',
      label: this.translate.instant('battery_pass.edit.schemas.cx_pcf'),
    },
    {
      value: 'pathera_declarableSubstances_0.9.0',
      label: this.translate.instant(
        'battery_pass.edit.schemas.pathera_declarable_substances',
      ),
    },
    {
      value: 'pathera_pcf_1.0.0',
      label: this.translate.instant('battery_pass.edit.schemas.pathera_pcf'),
    },
    {
      value: 'pathera_secondaryMaterialContent_1.0.0',
      label: this.translate.instant(
        'battery_pass.edit.schemas.pathera_secondary_material_content',
      ),
    },
    {
      value: 'pathera_batteryTypeInformation_1.0.0',
      label: this.translate.instant(
        'battery_pass.edit.schemas.pathera_battery_type_info',
      ),
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  ngOnInit() {
    this.providers = this.activeModal.data.providers;
    this.initialAttributeLabelData =
      this.activeModal.data.attributeLabelData.filter(
        (label: BatteryPassAttributeLabel) => label.status === 'open',
      );
    this.cxSearchForm = new FormGroup({
      assignedProviders: new FormControl(null, Validators.required),
      assignedSchema: new FormControl(null, Validators.required),
      assetId: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^CX:[a-z0-9A-Z-._]{8,64}:[a-z0-9A-Z-._]{8,64}$/),
      ]),
    });
  }

  onProviderChange(evt: CustomEvent) {
    this.cxSearchForm.controls['assignedProviders'].setValue(evt.detail);
  }

  isIndexEnabled(index: number): boolean {
    if (index === this.SEARCH_ASSETS_TAB) {
      return true;
    } else if (index === this.COMPARE_ASSETS_TAB) {
      return (
        this.cxSearchForm.value.assignedProviders !== null &&
        this.cxSearchForm.value.assignedSchema !== null
      );
    } else if (index === this.IMPORT_ASSETS_TAB) {
      return this.isSomethingToMap;
    } else {
      return false;
    }
  }

  async onSelectIndexClick(index: number) {
    if (this.isIndexEnabled(index)) {
      this.selectedIndex = index;
      if (index === this.COMPARE_ASSETS_TAB) {
        this.mapWantedToReceivedAttributes();
      }
    }
  }

  onSchemaChange(evt: CustomEvent) {
    this.selectedSchema = this.availableSchemas.filter(
      (schema) => schema.value === evt.detail,
    )[0].label;
    this.cxSearchForm.controls['assignedSchema'].setValue(evt.detail);
  }

  async onCxSearchClick() {
    this.cxSearchForm.markAllAsTouched();
    this.searchErrors = [];

    if (this.cxSearchForm.valid) {
      this.importSearchLoading = true;
      this.initialAttributeLabelData = this.initialAttributeLabelData.filter(
        (assignment) =>
          assignment.assigneeEmail ===
          this.cxSearchForm.value.assignedProviders,
      );
      try {
        this.model = await this.passportService.loadOne(
          this.cxSearchForm.value.assetId,
          true,
          this.cxSearchForm.value.assignedSchema,
        );
        const flattenPass = this.flattenBatteryPass(this.model);
        this.receivedData = flattenPass
          .filter(
            (value) => value.value !== '' && !value.key.startsWith('metadata'),
          )
          .map((value) => ({
            ...value,
            key: value.key.split('.').slice(1).join('.'),
          }));
        this.mapWantedToReceivedAttributes();
        this.selectedIndex = this.COMPARE_ASSETS_TAB;
      } catch (error) {
        this.searchErrors = [];
        const errorResponse = (
          error as {
            error: {response?: {error: string; name: string}};
          }
        ).error.response;
        if (errorResponse?.error === 'invalid_client') {
          this.searchErrors.push(
            this.translate.instant('battery_pass.import.invalid_client'),
          );
        } else if (errorResponse?.name === 'InternalServerErrorException') {
          this.searchErrors.push(
            this.translate.instant('battery_pass.import.pass_not_found', {
              id: this.cxSearchForm.value.assetId,
            }),
          );
        } else {
          this.searchErrors.push(
            this.translate.instant('battery_pass.import.import_failed_text'),
          );
        }
      }
    }
    this.importSearchLoading = false;
  }

  async importAsset({data: passport}: PassportDto) {
    await this.onSelectIndexClick(this.IMPORT_ASSETS_TAB);
    this.importLoading = true;
    this.hasImportStarted = true;
    try {
      const filteredMappedData = this.stripAttributesForAssignments(
        this.initialAttributeLabelData,
        passport,
      );
      await this.passportService.saveAttributeAssignments(
        {data: filteredMappedData},
        'API',
        false,
        true,
      );
      this.isImportSuccessful = true;
    } catch {
      this.importErrors = [];
      this.importErrors.push(
        this.translate.instant('battery_pass.import.import_failed_text'),
      );
      this.isImportSuccessful = false;
    }
    this.importLoading = false;
  }

  stripAttributesForAssignments(
    assignments: BatteryPassAttributeLabel[],
    passport: Partial<PassportDto>,
  ) {
    const mappedAssignments = assignments.map(
      (assignment): ApiAssignmentPatchDto => {
        const value = this.dataHandlingService.getValueByPath(
          passport,
          assignment.attribute,
        );
        return {
          id: assignment.id ?? '',
          value: value as string,
        };
      },
    );
    return mappedAssignments.filter(
      (assignment) =>
        assignment.value !== null &&
        assignment.value !== '' &&
        assignment.value !== undefined,
    );
  }

  flattenBatteryPass(batteryPass: PassportDto): {
    key: string;
    value: string;
    found: boolean;
  }[] {
    const attributes = this.dataHandlingService.getNestedKeys(batteryPass);
    return attributes.map((key) => {
      const value =
        this.dataHandlingService.getValueByPath(batteryPass, key) ?? '';
      return {key, value: value, found: false};
    });
  }

  mapWantedToReceivedAttributes() {
    this.expectedData = this.initialAttributeLabelData.map(
      ({attribute, attributeLabel}: BatteryPassAttributeLabel) => {
        const receivedAttribute = this.receivedData.find(
          (data) => data.key === attribute,
        );
        if (receivedAttribute) {
          this.isSomethingToMap = true;
        }
        return {
          key: attributeLabel || attribute,
          value: '',
          found: !!receivedAttribute,
        };
      },
    );
    this.receivedData = this.receivedData.map((data) => {
      return {
        key: data.key,
        value: data.value,
        found: !!this.expectedData.find(
          (expected) => expected.key === data.key,
        ),
      };
    });
    this.expectedData.sort(
      (a, b) => Number(b.found) - Number(a.found) || a.key.localeCompare(b.key),
    );
    this.receivedData.sort(
      (a, b) => Number(b.found) - Number(a.found) || a.key.localeCompare(b.key),
    );
  }
}
